import { useNavigate } from 'react-router-dom';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { HomeSection } from '@/services/homeSections';
import { withImageFallback } from '@/utils/images';

type PromoBannerProps = {
  section?: HomeSection | null;
};

const fallbackPromo = {
  eyebrow: 'NEW ARRIVAL',
  badge: '新品體驗',
  title: '新一代聽力照護體驗',
  description: '從檢測、選配到後續調整，讓每一次配戴都更貼近日常。',
  buttonLabel: '立即預約諮詢',
  buttonUrl: '/booking',
  image: 'https://readdy.ai/api/search-image?query=new%20hearing%20aid%20product%20launch%20promotion%20modern%20device%20on%20warm%20orange%20gradient%20background%20professional%20product%20photography%20high%20quality%20close%20up%20soft%20lighting&width=800&height=480&seq=31&orientation=landscape',
  imageAlt: '助聽器新品形象照',
};

function formatLines(text: string) {
  return text.split(/\r?\n|[，,]/).map((part) => part.trim()).filter(Boolean);
}

export default function PromoBanner({ section }: PromoBannerProps) {
  const navigate = useNavigate();
  const { ref: leftRef, isVisible: leftVisible } = useScrollReveal();
  const { ref: rightRef, isVisible: rightVisible } = useScrollReveal();
  const titleLines = formatLines(section?.Title || fallbackPromo.title);
  const subtitleLines = formatLines(section?.Subtitle || section?.Title || fallbackPromo.title);
  const buttonUrl = section?.PrimaryButtonUrl || fallbackPromo.buttonUrl;
  const promoTags = section?.Tags?.filter(Boolean) || [];

  function go(url: string) {
    if (/^https?:\/\//i.test(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(url);
  }

  return (
    <section className="w-full">
      <div className="flex flex-col md:flex-row">
        <div
          ref={leftRef}
          className={`relative w-full md:w-1/2 min-h-[300px] md:min-h-[480px] overflow-hidden scroll-reveal ${leftVisible ? 'scroll-reveal-visible' : ''}`}
        >
          <img
            src={withImageFallback(section?.ImageUrl || fallbackPromo.image)}
            alt={section?.ImageAlt || fallbackPromo.imageAlt}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1B3D2A]/85 via-[#1B3D2A]/40 to-transparent" />
          <div className="absolute top-6 left-6">
            <span className="text-sm text-white/80 font-medium bg-[#1B3D2A]/40 px-3 py-1 rounded-full">
              {section?.BadgeText || fallbackPromo.badge}
            </span>
          </div>
          <div className="absolute bottom-6 left-6 right-6">
            <h3 className="text-white text-2xl md:text-3xl font-bold leading-tight">
              {titleLines.map((line) => (
                <span className="block" key={line}>{line}</span>
              ))}
            </h3>
          </div>
        </div>

        <div
          ref={rightRef}
          className={`w-full md:w-1/2 bg-[#FAF8F5] flex flex-col items-center justify-center px-6 md:px-12 py-10 md:py-0 min-h-[300px] md:min-h-[480px] scroll-reveal scroll-reveal-delay-2 ${rightVisible ? 'scroll-reveal-visible' : ''}`}
        >
          <span className="inline-block text-xs font-semibold tracking-widest text-[#D4A574] uppercase mb-2">
            {section?.Eyebrow || fallbackPromo.eyebrow}
          </span>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#1B3D2A] text-center leading-tight">
            {subtitleLines.map((line) => (
              <span className="block" key={line}>{line}</span>
            ))}
          </h2>
          <p className="mt-6 text-[#5A6B5A] text-base md:text-lg text-center leading-relaxed max-w-sm">
            {section?.Description || fallbackPromo.description}
          </p>
          {promoTags.length > 0 && (
            <div className="mt-5 flex max-w-sm flex-wrap justify-center gap-2">
              {promoTags.map((tag) => (
                <span className="rounded-full border border-[#E8E0D6] bg-white px-3 py-1.5 text-xs font-bold text-[#1B3D2A]" key={tag}>
                  {tag}
                </span>
              ))}
            </div>
          )}
          {section?.NoteText && (
            <p className="mt-4 max-w-sm text-center text-sm font-medium text-[#9A8A7A]">
              {section.NoteText}
            </p>
          )}
          <button
            type="button"
            onClick={() => go(buttonUrl)}
            className="mt-6 md:mt-8 bg-[#1B3D2A] text-white px-6 md:px-8 py-3 md:py-4 rounded-full text-base font-medium hover:bg-[#143026] transition-colors flex items-center gap-2 whitespace-nowrap"
          >
            {section?.PrimaryButtonLabel || fallbackPromo.buttonLabel}
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-base"></i>
            </div>
          </button>
        </div>
      </div>
    </section>
  );
}
